import { FC, useMemo, useEffect, useState } from 'react';
import {
  SimpleGrid,
  Paper,
  Stack,
  Text,
  Group,
  ThemeIcon,
  Skeleton,
} from '@mantine/core';
import {
  IconBuildingHospital,
  IconFileInvoice,
  IconPrescription,
  IconCoin,
  IconDotsCircleHorizontal,
} from '@tabler/icons-react';
import { Invoice, ChargeItem } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useTranslation } from 'react-i18next';
import { PaymentSource } from '../lims-types';
import { getPaymentSource, getLinkedOrderRef, getDiscount } from '../utils/payment-helpers';
import { ARKON } from '@consts/systems.consts';

// ---------------------------------------------------------------------------

interface SourceStat {
  source: PaymentSource | 'OTHER';
  label: string;
  icon: React.ReactNode;
  color: string;
  count: number;
  totalGross: number;
  totalNet: number;
  currency: string;
}

const DEPOSIT_CODE = ARKON.CS.CHARGE_ITEM.CODES.DEPOSIT;
const CHARGE_ITEM_BASE = ARKON.CS.CHARGE_ITEM.BASE;

function isDepositItem(ci: ChargeItem): boolean {
  return (
    ci.code?.coding?.some(
      (c) => c.system === CHARGE_ITEM_BASE && c.code === DEPOSIT_CODE
    ) ?? false
  );
}

function formatAmount(value: number, currency: string): string {
  return `${value.toFixed(2)} ${currency}`;
}

// ---------------------------------------------------------------------------

interface PaymentSummaryCardsProps {
  invoices: Invoice[];
}

export const PaymentSummaryCards: FC<PaymentSummaryCardsProps> = ({ invoices }) => {
  const { t } = useTranslation('lims');
  const medplum = useMedplum();

  const [chargeItems, setChargeItems] = useState<ChargeItem[]>([]);
  const [loading, setLoading] = useState(false);

  // Collect unique service-request IDs from the invoices
  const orderRefs = useMemo(() => {
    const refs = new Set<string>();
    for (const inv of invoices) {
      const ref = getLinkedOrderRef(inv);
      if (ref) refs.add(ref);
    }
    return Array.from(refs);
  }, [invoices]);

  // Fetch main ChargeItems (exclude deposits) for those orders
  useEffect(() => {
    if (orderRefs.length === 0) {
      setChargeItems([]);
      return;
    }

    let cancelled = false;
    setLoading(true);

    Promise.all(
      orderRefs.map((ref) =>
        medplum.searchResources('ChargeItem', {
          service: ref,
          _count: '200',
        })
      )
    )
      .then((results) => {
        if (cancelled) return;
        const all: ChargeItem[] = results.flat().filter((ci) => !isDepositItem(ci));
        setChargeItems(all);
      })
      .catch(console.error)
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [orderRefs, medplum]);

  // Aggregate by payment source
  const stats = useMemo<SourceStat[]>(() => {
    const buckets: Record<
      string,
      { count: number; gross: number; net: number; currency: string; seen: Set<string> }
    > = {
      [PaymentSource.HOSPITAL]: { count: 0, gross: 0, net: 0, currency: '€', seen: new Set() },
      [PaymentSource.ELKE]: { count: 0, gross: 0, net: 0, currency: '€', seen: new Set() },
      [PaymentSource.EPI]: { count: 0, gross: 0, net: 0, currency: '€', seen: new Set() },
      [PaymentSource.PRESCRIPTION]: { count: 0, gross: 0, net: 0, currency: '€', seen: new Set() },
      OTHER: { count: 0, gross: 0, net: 0, currency: '€', seen: new Set() },
    };

    for (const ci of chargeItems) {
      const src = getPaymentSource(ci);
      const key =
        src && Object.values(PaymentSource).includes(src as PaymentSource) ? src : 'OTHER';
      const bucket = buckets[key];
      if (!bucket) continue;

      // net = priceOverride.value (already discounted)
      const net = ci.priceOverride?.value ?? 0;
      const discount = getDiscount(ci);
      const gross = net + discount;
      const currency = ci.priceOverride?.currency ?? '€';

      bucket.gross += gross;
      bucket.net += net;
      if (currency) bucket.currency = currency;

      // Count distinct orders (service refs)
      const svcRef = ci.service?.[0]?.reference;
      if (svcRef) {
        bucket.seen.add(svcRef);
      }
    }

    const definitions: Omit<SourceStat, 'count' | 'totalGross' | 'totalNet' | 'currency'>[] = [
      {
        source: PaymentSource.HOSPITAL,
        label: t('payment.sources.hospital', 'Hospital', { ns: 'lims' }),
        icon: <IconBuildingHospital size={20} />,
        color: 'blue',
      },
      {
        source: PaymentSource.ELKE,
        label: t('payment.sources.elke', 'ELKE', { ns: 'lims' }),
        icon: <IconFileInvoice size={20} />,
        color: 'teal',
      },
      {
        source: PaymentSource.EPI,
        label: t('payment.sources.epi', 'EPI', { ns: 'lims' }),
        icon: <IconCoin size={20} />,
        color: 'violet',
      },
      {
        source: PaymentSource.PRESCRIPTION,
        label: t('payment.sources.prescription', 'Prescription', { ns: 'lims' }),
        icon: <IconPrescription size={20} />,
        color: 'orange',
      },
      {
        source: 'OTHER',
        label: t('payment.sources.other', 'Other', { ns: 'lims' }),
        icon: <IconDotsCircleHorizontal size={20} />,
        color: 'gray',
      },
    ];

    return definitions.map((def) => {
      const b = buckets[def.source] ?? { count: 0, gross: 0, net: 0, currency: '€', seen: new Set() };
      return {
        ...def,
        count: b.seen.size,
        totalGross: b.gross,
        totalNet: b.net,
        currency: b.currency,
      };
    });
  }, [chargeItems, t]);

  if (loading) {
    return (
      <SimpleGrid cols={{ base: 2, sm: 3, lg: 5 }} spacing="sm">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} height={120} radius="md" />
        ))}
      </SimpleGrid>
    );
  }

  return (
    <SimpleGrid cols={{ base: 2, sm: 3, lg: 5 }} spacing="sm">
      {stats.map((stat) => (
        <Paper key={stat.source} shadow="xs" withBorder p="md" radius="md">
          <Stack gap={6}>
            <Group justify="space-between" align="flex-start">
              <Text size="sm" fw={600} c="dimmed" tt="uppercase" lts={0.5}>
                {stat.label}
              </Text>
              <ThemeIcon color={stat.color} variant="light" size="md" radius="xl">
                {stat.icon}
              </ThemeIcon>
            </Group>

            <Text size="xl" fw={800} lh={1.1}>
              {stat.count}
            </Text>
            <Text size="xs" c="dimmed">
              {t('paymentsPage.summary.orders', '{{count}} order(s)', {
                ns: 'lims',
                count: stat.count,
              })}
            </Text>

            <Stack gap={2} mt={4}>
              <Group justify="space-between">
                <Text size="xs" c="dimmed">
                  {t('paymentsPage.summary.gross', 'Gross', { ns: 'lims' })}
                </Text>
                <Text size="xs" fw={500}>
                  {formatAmount(stat.totalGross, stat.currency)}
                </Text>
              </Group>
              <Group justify="space-between">
                <Text size="xs" c="dimmed">
                  {t('paymentsPage.summary.net', 'Net', { ns: 'lims' })}
                </Text>
                <Text size="xs" fw={600} c={stat.color}>
                  {formatAmount(stat.totalNet, stat.currency)}
                </Text>
              </Group>
            </Stack>
          </Stack>
        </Paper>
      ))}
    </SimpleGrid>
  );
};
